import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import PropTypes from 'prop-types'
import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCalculateTypography } from 'src/editor/shared/hooks/use-calculate-typography'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import MobileIconUi from '../styles/ui/MobileIconUi'
import ResetIcon from './ui/ResetIcon'

/**
 * TextFontSize Component Props
 * @typedef {Object} TextFontSizeProps
 * @param {(string|number)} [fontSize] - The font size value for desktop view
 * @param {(string|number)} [mobileFontSize] - The font size value for mobile view
 * @param {(string)} [typographyType] - The typography type (e.g., 'heading', 'paragraph') to calculate correct global fonts
 * @param {Function} update - Function to update the font size for desktop view
 * @param {Function} mobileUpdate - Function to update the font size for mobile view
 * @param {Function} removeEntityStyle - Function to remove style from entity in desktop view
 * @param {Function} removeEntityMobileStyle - Function to remove style from entity in mobile view
 * @param {string} [labelText] - Text label for the font size control
 */
function TextFontSize({
  fontSize = null,
  mobileFontSize = null,
  labelText = 'settings_styles.font_size.label',
  typographyType,
  ...rest
}) {
  const { t } = useTranslation()
  const { isMobile } = useDeviceModeContext()

  const { globalFontSize, globalMobileFontSize } = useCalculateTypography({
    typographyType,
  })
  const update = value =>
    isMobile ? rest.mobileUpdate(value) : rest.update(value)

  const reset = () =>
    isMobile ? rest.removeEntityMobileStyle() : rest.removeEntityStyle()

  const currentGlobalFontSize = isMobile ? globalMobileFontSize : globalFontSize
  const currentFontSize = isMobile ? mobileFontSize : fontSize
  const isGlobal = currentFontSize === undefined || currentFontSize === null

  const calculatedFontSize = currentFontSize ?? currentGlobalFontSize

  return (
    <RangeElement
      label={labelText}
      labelIcon={
        <React.Fragment>
          {isMobile && <MobileIconUi />}
          {!isGlobal && (
            <ResetIcon
              onClick={reset}
              title={t('settings_styles.font_size.inherit')}
            />
          )}
        </React.Fragment>
      }
      value={parseFloat(calculatedFontSize, 0)}
      onChange={update}
      min={8}
      isDecimalAllowed
    />
  )
}

TextFontSize.propTypes = {
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mobileFontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  typographyType: PropTypes.string,
  update: PropTypes.func.isRequired,
  mobileUpdate: PropTypes.func.isRequired,
  removeEntityMobileStyle: PropTypes.func.isRequired,
  removeEntityStyle: PropTypes.func.isRequired,
  labelText: PropTypes.string,
}

export default memo(TextFontSize)
